import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PAUnitType, PAServiceType, ServiceCategory } from '../../../../dtos/patientmr/authorizations.dto';
import { AuthorizationsService, SERVICE_CATEGORY_CONFIG } from '../../../../services/patientmr/authorizations.service';
import PrimaryButton from '../../../common/PrimaryButton';
import PrimaryButtonLight from '../../../common/PrimaryButtonLight';

interface ApprovalFormData {
  authNumber: string;
  approvedStartDate: string;
  approvedEndDate: string;
  approvedQuantity: number | string;
  approvedPerWeek: number | string;
  serviceType: string;
  uaApproved?: boolean;
}

interface ApprovalSectionProps {
  // Read-only mode (for viewing approved PAs)
  readOnly?: boolean;
  defaultOpen?: boolean;
  // PA context
  unitType: PAUnitType | string;
  minutesPerUnit: number | null;
  serviceCategory: ServiceCategory | string;
  requestedStartDate?: string;
  requestedEndDate?: string;
  // Approval data (read-only)
  authNumber?: string | null;
  approvedStartDate?: string | null;
  approvedEndDate?: string | null;
  approvedQuantity?: number | null;
  approvedPerWeek?: number | null;
  serviceType?: PAServiceType | string | null;
  uaApproved?: boolean | null;
  // Editable approval form
  formMode?: boolean;
  formData?: ApprovalFormData;
  onFormChange?: (field: string, value: string | number | boolean) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  saving?: boolean;
}

const SERVICE_TYPE_OPTS = [
  { value: PAServiceType.RN_ONLY, label: 'RN only' },
  { value: PAServiceType.LVN_ONLY, label: 'LVN only' },
  { value: PAServiceType.RN_AND_LVN, label: 'RN & LVN' }
];

export const ApprovalSection: React.FC<ApprovalSectionProps> = ({
  readOnly = false, defaultOpen = false, unitType, minutesPerUnit, serviceCategory,
  requestedStartDate, requestedEndDate,
  authNumber, approvedStartDate, approvedEndDate, approvedQuantity, approvedPerWeek, serviceType, uaApproved,
  formMode = false, formData, onFormChange, onConfirm, onCancel, saving = false
}) => {
  const [open, setOpen] = React.useState(formMode ? true : defaultOpen);
  const uLabel = AuthorizationsService.getUnitLabel(unitType);
  const pwLabel = AuthorizationsService.getPerWeekLabel(unitType);
  const showServiceType = SERVICE_CATEGORY_CONFIG[serviceCategory]?.showServiceType || false;
  const hasHours = unitType === PAUnitType.UNITS && minutesPerUnit;
  // Phase 17 (REQ-CLM-06): TMHP approves modifier UA on the PDN authorization itself
  const isPdn = String(serviceCategory).toLowerCase() === 'pdn';

  // Approval entry form
  if (formMode && formData && onFormChange) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-semibold text-emerald-800">Record Approval Details</h3>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">Auth Number <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={formData.authNumber}
              onChange={e => onFormChange('authNumber', e.target.value)}
              placeholder="From payer"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">Approved Start <span className="text-red-400">*</span></label>
            <input
              type="date"
              value={formData.approvedStartDate}
              onChange={e => onFormChange('approvedStartDate', e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">Approved End <span className="text-red-400">*</span></label>
            <input
              type="date"
              value={formData.approvedEndDate}
              onChange={e => onFormChange('approvedEndDate', e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
            />
          </div>
        </div>

        <div className={`grid gap-4 ${showServiceType ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
              Total Approved ({uLabel}) <span className="text-red-400">*</span>
            </label>
            <input
              type="number"
              value={formData.approvedQuantity}
              onChange={e => onFormChange('approvedQuantity', parseInt(e.target.value) || 0)}
              placeholder={unitType === PAUnitType.UNITS ? 'e.g. 480' : 'e.g. 24'}
              min={1}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
            />
            {hasHours && <p className="mt-1 text-xs text-slate-400">1 unit = {minutesPerUnit} min</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
              {pwLabel} <span className="text-red-400">*</span>
            </label>
            <input
              type="number"
              value={formData.approvedPerWeek}
              onChange={e => onFormChange('approvedPerWeek', parseInt(e.target.value) || 0)}
              placeholder={unitType === PAUnitType.UNITS ? 'e.g. 80' : 'e.g. 3'}
              min={1}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
            />
            <p className="mt-1 text-xs text-slate-400">As stated on approval</p>
          </div>
          {showServiceType && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Service Type <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.serviceType}
                onChange={e => onFormChange('serviceType', e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">Select...</option>
                {SERVICE_TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>

        {isPdn && (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={formData.uaApproved === true}
              onChange={e => onFormChange('uaApproved', e.target.checked)}
            />
            <span>
              Approved with modifier UA (invasive ventilation or tracheostomy)
              <span className="block text-xs text-slate-500">Check only if the payer&apos;s approval letter lists T1000 UA. Claims for this authorization then bill T1000 UA.</span>
            </span>
          </label>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-end gap-4 pt-4 border-t border-emerald-200">
          <div className="flex space-x-2 self-end">
            <PrimaryButtonLight onClick={onCancel} disabled={saving}>Cancel</PrimaryButtonLight>
            <PrimaryButton className="bg-emerald-600 hover:bg-emerald-700" onClick={onConfirm} disabled={saving}>
              {saving ? 'Saving...' : 'Confirm Approval'}
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  // Read-only display
  if (!readOnly) return null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50" type="button">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-semibold text-slate-800">Approval Details</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Auth Number</dt>
              <dd className="mt-0.5 text-sm text-slate-900 font-mono">{authNumber || '\u2014'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Approved Start</dt>
              <dd className="mt-0.5 text-sm text-slate-900">{approvedStartDate ? AuthorizationsService.formatDate(approvedStartDate) : '\u2014'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Approved End</dt>
              <dd className="mt-0.5 text-sm text-slate-900">{approvedEndDate ? AuthorizationsService.formatDate(approvedEndDate) : '\u2014'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Approved</dt>
              <dd className="mt-0.5 text-sm text-slate-900">
                {approvedQuantity != null ? AuthorizationsService.formatQuantity(approvedQuantity, unitType, minutesPerUnit) : '\u2014'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">{pwLabel}</dt>
              <dd className="mt-0.5 text-sm text-slate-900">
                {approvedPerWeek != null
                  ? (hasHours ? `${approvedPerWeek} (${AuthorizationsService.toHours(approvedPerWeek, unitType, minutesPerUnit)} hrs)` : String(approvedPerWeek))
                  : '\u2014'}
              </dd>
            </div>
            {showServiceType && serviceType && (
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Service Type</dt>
                <dd className="mt-0.5 text-sm text-slate-900">{serviceType}</dd>
              </div>
            )}
            {isPdn && (
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Modifier UA</dt>
                <dd className="mt-0.5 text-sm text-slate-900">{uaApproved ? 'Approved' : 'Not approved'}</dd>
              </div>
            )}
            {minutesPerUnit && (
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Unit Definition</dt>
                <dd className="mt-0.5 text-sm text-slate-900">{AuthorizationsService.getDurationLabel(minutesPerUnit)}</dd>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalSection;
